import React, { Component } from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Cell } from 'recharts';
const OEEDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'OEE %',
    value: '78.5%',
    change: '+2.8%',
    isPositive: true,
    subtitle: 'Overall Equipment Effectiveness'
  }, {
    title: 'Availability %',
    value: '89.5%',
    change: '+1.5%',
    isPositive: true,
    subtitle: 'Uptime performance'
  }, {
    title: 'Performance %',
    value: '93.2%',
    change: '+1.8%',
    isPositive: true,
    subtitle: 'Speed efficiency'
  }, {
    title: 'Quality %',
    value: '96.9%',
    change: '+0.5%',
    isPositive: true,
    subtitle: 'First pass yield'
  }];
  // OEE Loss Tree Data (Waterfall Chart)
  const oeeLossTreeData = [{
    category: 'Planned Production Time',
    value: 100,
    displayValue: 100,
    color: '#10b981'
  }, {
    category: 'Availability Losses',
    value: -10.5,
    displayValue: 89.5,
    color: '#ef4444'
  }, {
    category: 'Performance Losses',
    value: -6.8,
    displayValue: 82.7,
    color: '#f59e0b'
  }, {
    category: 'Quality Losses',
    value: -4.2,
    displayValue: 78.5,
    color: '#3b82f6'
  }, {
    category: 'Final OEE',
    value: 78.5,
    displayValue: 78.5,
    color: '#8b5cf6'
  }];
  // Detailed Loss Breakdown
  const lossBreakdownData = {
    availability: [{
      type: 'Planned Downtime',
      hours: 24,
      percentage: 2.5
    }, {
      type: 'Unplanned Downtime',
      hours: 42,
      percentage: 4.4
    }, {
      type: 'Setup & Changeover',
      hours: 28,
      percentage: 2.9
    }, {
      type: 'Equipment Failure',
      hours: 8,
      percentage: 0.8
    }],
    performance: [{
      type: 'Minor Stoppages',
      hours: 32,
      percentage: 3.3
    }, {
      type: 'Reduced Speed',
      hours: 28,
      percentage: 2.9
    }, {
      type: 'Idling & Waiting',
      hours: 8,
      percentage: 0.8
    }],
    quality: [{
      type: 'Scrap & Rework',
      units: 1450,
      percentage: 2.1
    }, {
      type: 'Startup Losses',
      units: 850,
      percentage: 1.2
    }, {
      type: 'Process Defects',
      units: 620,
      percentage: 0.9
    }]
  };
  // OEE Trend Chart Data (Last 12 Months)
  const oeeTrendData = [{
    month: 'Jan',
    oee: 75.2,
    availability: 85.2,
    performance: 90.5,
    quality: 97.5,
    target: 80
  }, {
    month: 'Feb',
    oee: 76.1,
    availability: 85.8,
    performance: 91.0,
    quality: 97.6,
    target: 80
  }, {
    month: 'Mar',
    oee: 76.8,
    availability: 86.2,
    performance: 91.2,
    quality: 97.7,
    target: 80
  }, {
    month: 'Apr',
    oee: 77.2,
    availability: 86.5,
    performance: 91.5,
    quality: 97.8,
    target: 80
  }, {
    month: 'May',
    oee: 77.8,
    availability: 87.0,
    performance: 91.8,
    quality: 97.5,
    target: 80
  }, {
    month: 'Jun',
    oee: 78.5,
    availability: 87.5,
    performance: 92.0,
    quality: 97.6,
    target: 80
  }, {
    month: 'Jul',
    oee: 78.8,
    availability: 87.8,
    performance: 92.2,
    quality: 97.4,
    target: 80
  }, {
    month: 'Aug',
    oee: 79.2,
    availability: 88.2,
    performance: 92.5,
    quality: 97.1,
    target: 80
  }, {
    month: 'Sep',
    oee: 78.5,
    availability: 87.8,
    performance: 92.0,
    quality: 97.2,
    target: 80
  }, {
    month: 'Oct',
    oee: 79.5,
    availability: 88.5,
    performance: 92.8,
    quality: 96.9,
    target: 80
  }, {
    month: 'Nov',
    oee: 80.2,
    availability: 89.0,
    performance: 93.0,
    quality: 96.8,
    target: 80
  }, {
    month: 'Dec',
    oee: 80.8,
    availability: 89.5,
    performance: 93.2,
    quality: 96.9,
    target: 80
  }];
  // Line-wise OEE Comparison
  const lineOEEData = [{
    line: 'Line 1',
    oee: 76.5,
    availability: 87.2,
    performance: 91.5,
    quality: 96.0
  }, {
    line: 'Line 2',
    oee: 82.0,
    availability: 90.5,
    performance: 93.5,
    quality: 97.0
  }, {
    line: 'Line 3',
    oee: 75.0,
    availability: 86.0,
    performance: 90.5,
    quality: 96.5
  }, {
    line: 'Line 4',
    oee: 79.0,
    availability: 88.5,
    performance: 92.0,
    quality: 97.0
  }, {
    line: 'Line 5',
    oee: 77.8,
    availability: 87.8,
    performance: 91.8,
    quality: 96.5
  }];
  // Shift-wise OEE Comparison
  const shiftOEEData = [{
    shift: 'Shift A\n(6-14)',
    oee: 80.2,
    availability: 89.8,
    performance: 92.5,
    quality: 96.5
  }, {
    shift: 'Shift B\n(14-22)',
    oee: 82.5,
    availability: 91.2,
    performance: 93.8,
    quality: 96.8
  }, {
    shift: 'Shift C\n(22-6)',
    oee: 72.8,
    availability: 84.5,
    performance: 89.5,
    quality: 96.2
  }];
  // OEE by Plant Table Data
  const plantOEEData = [{
    plant: 'Manesar',
    oee: 76.5,
    availability: 87.2,
    performance: 91.5,
    quality: 96.0,
    plannedTime: 720,
    actualTime: 628,
    downtime: 92,
    idealCycles: 68600,
    actualCycles: 62769,
    goodParts: 60258,
    defects: 2511,
    target: 80,
    gap: -3.5
  }, {
    plant: 'Chennai',
    oee: 82.0,
    availability: 90.5,
    performance: 93.5,
    quality: 97.0,
    plannedTime: 720,
    actualTime: 652,
    downtime: 68,
    idealCycles: 72500,
    actualCycles: 67788,
    goodParts: 65754,
    defects: 2034,
    target: 80,
    gap: 2.0
  }, {
    plant: 'Pune',
    oee: 75.0,
    availability: 86.0,
    performance: 90.5,
    quality: 96.5,
    plannedTime: 720,
    actualTime: 619,
    downtime: 101,
    idealCycles: 65800,
    actualCycles: 59549,
    goodParts: 57465,
    defects: 2084,
    target: 80,
    gap: -5.0
  }, {
    plant: 'Bangalore',
    oee: 79.0,
    availability: 88.5,
    performance: 92.0,
    quality: 97.0,
    plannedTime: 720,
    actualTime: 637,
    downtime: 83,
    idealCycles: 69200,
    actualCycles: 63664,
    goodParts: 61754,
    defects: 1910,
    target: 80,
    gap: -1.0
  }];
  const getOEEColor = (oee: number) => {
    if (oee >= 85) return 'bg-green-100 text-green-700';
    if (oee >= 80) return 'bg-blue-100 text-blue-700';
    if (oee >= 75) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };
  const getGapColor = (gap: number) => {
    if (gap >= 0) return 'text-green-600';
    return 'text-red-600';
  };
  // Custom Waterfall Chart Component
  const WaterfallChart = () => {
    let cumulativeValue = 0;
    const chartData = oeeLossTreeData.map((item, index) => {
      let start = cumulativeValue;
      if (index === 0) {
        cumulativeValue = item.value;
        return {
          ...item,
          start: 0,
          end: item.value,
          isPositive: true
        };
      } else if (index === oeeLossTreeData.length - 1) {
        return {
          ...item,
          start: 0,
          end: item.value,
          isPositive: true
        };
      } else {
        cumulativeValue += item.value;
        return {
          ...item,
          start: Math.min(start, cumulativeValue),
          end: Math.max(start, cumulativeValue),
          isPositive: item.value >= 0
        };
      }
    });
    return <ResponsiveContainer width="100%" height={400} data-id="element-5755">
        <ComposedChart data={chartData} data-id="element-5756">
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5757" />
          <XAxis dataKey="category" angle={-20} textAnchor="end" height={100} tick={{
          fontSize: 11
        }} data-id="element-5758" />
          <YAxis domain={[0, 100]} tick={{
          fontSize: 12
        }} label={{
          value: 'OEE %',
          angle: -90,
          position: 'insideLeft',
          style: {
            fontSize: 12
          }
        }} data-id="element-5759" />
          <Tooltip contentStyle={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }} formatter={(value: number, name: string) => {
          if (name === 'Loss' || name === 'Gain') {
            return [`${Math.abs(value).toFixed(1)}%`, name];
          }
          return [`${value.toFixed(1)}%`, name];
        }} data-id="element-5760" />
          <Bar dataKey="start" stackId="a" fill="transparent" data-id="element-5761" />
          <Bar dataKey="value" stackId="a" name="Loss" data-id="element-5762">
            {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-5763" />)}
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>;
  };
  return <div data-id="element-5764">
      <DescriptionPanel title="OEE (Overall Equipment Effectiveness)" description="Comprehensive OEE analysis tracking Availability × Performance × Quality across all production lines. Identify losses, optimize equipment utilization, and drive world-class manufacturing performance." insights={['Overall OEE at 78.5% - approaching world-class benchmark of 85%', 'Chennai plant leading with 82% OEE - best practices to replicate', 'Availability losses (10.5%) are the primary opportunity - focus on reducing downtime', 'Shift C underperforming at 72.8% OEE - night shift optimization needed']} data-id="element-5765" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-5766">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-5767">
          OEE Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-5768">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-5769">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-5770">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-5771">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-5772">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-5773">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-5774" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-5775" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* OEE Loss Tree */}
      <Card className="p-6 mb-8" data-id="element-5776">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5777">
          OEE Loss Tree Analysis
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5778">
          Waterfall breakdown showing how losses reduce ideal production to
          actual OEE
        </p>
        <WaterfallChart data-id="element-5779" />
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5780">
          <div className="grid grid-cols-4 gap-4" data-id="element-5781">
            <div className="text-center" data-id="element-5782">
              <div className="text-sm text-gray-600" data-id="element-5783">Planned Time</div>
              <div className="text-2xl font-bold text-green-600" data-id="element-5784">100%</div>
              <div className="text-xs text-gray-500" data-id="element-5785">720 hrs/month</div>
            </div>
            <div className="text-center" data-id="element-5786">
              <div className="text-sm text-gray-600" data-id="element-5787">Availability Loss</div>
              <div className="text-2xl font-bold text-red-600" data-id="element-5788">-10.5%</div>
              <div className="text-xs text-gray-500" data-id="element-5789">76 hrs downtime</div>
            </div>
            <div className="text-center" data-id="element-5790">
              <div className="text-sm text-gray-600" data-id="element-5791">Performance Loss</div>
              <div className="text-2xl font-bold text-orange-600" data-id="element-5792">-6.8%</div>
              <div className="text-xs text-gray-500" data-id="element-5793">Speed losses</div>
            </div>
            <div className="text-center" data-id="element-5794">
              <div className="text-sm text-gray-600" data-id="element-5795">Quality Loss</div>
              <div className="text-2xl font-bold text-blue-600" data-id="element-5796">-4.2%</div>
              <div className="text-xs text-gray-500" data-id="element-5797">Defects & rework</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Loss Breakdown Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" data-id="element-5798">
        {/* Availability Losses */}
        <Card className="p-6" data-id="element-5799">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5800">
            Availability Losses Breakdown
          </h3>
          <div className="space-y-3" data-id="element-5801">
            {lossBreakdownData.availability.map((item, index) => <div key={index} data-id="element-5802">
                <div className="flex items-center justify-between mb-1" data-id="element-5803">
                  <span className="text-sm font-medium text-gray-700" data-id="element-5804">
                    {item.type}
                  </span>
                  <div className="text-right" data-id="element-5805">
                    <span className="text-sm font-semibold text-gray-800" data-id="element-5806">
                      {item.hours}h
                    </span>
                    <span className="text-xs text-gray-600 ml-2" data-id="element-5807">
                      ({item.percentage}%)
                    </span>
                  </div>
                </div>
                <div className="relative h-4 bg-gray-100 rounded overflow-hidden" data-id="element-5808">
                  <div className="h-full bg-red-500" style={{
                width: `${item.percentage / 10.5 * 100}%`
              }} data-id="element-5809" />
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 text-center" data-id="element-5810">
            <div className="text-sm text-gray-600" data-id="element-5811">Total Availability Loss</div>
            <div className="text-2xl font-bold text-red-600" data-id="element-5812">102 hrs</div>
            <div className="text-xs text-gray-500" data-id="element-5813">10.5% of planned time</div>
          </div>
        </Card>
        {/* Performance Losses */}
        <Card className="p-6" data-id="element-5814">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5815">
            Performance Losses Breakdown
          </h3>
          <div className="space-y-3" data-id="element-5816">
            {lossBreakdownData.performance.map((item, index) => <div key={index} data-id="element-5817">
                <div className="flex items-center justify-between mb-1" data-id="element-5818">
                  <span className="text-sm font-medium text-gray-700" data-id="element-5819">
                    {item.type}
                  </span>
                  <div className="text-right" data-id="element-5820">
                    <span className="text-sm font-semibold text-gray-800" data-id="element-5821">
                      {item.hours}h
                    </span>
                    <span className="text-xs text-gray-600 ml-2" data-id="element-5822">
                      ({item.percentage}%)
                    </span>
                  </div>
                </div>
                <div className="relative h-4 bg-gray-100 rounded overflow-hidden" data-id="element-5823">
                  <div className="h-full bg-orange-500" style={{
                width: `${item.percentage / 6.8 * 100}%`
              }} data-id="element-5824" />
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 text-center" data-id="element-5825">
            <div className="text-sm text-gray-600" data-id="element-5826">Total Performance Loss</div>
            <div className="text-2xl font-bold text-orange-600" data-id="element-5827">68 hrs</div>
            <div className="text-xs text-gray-500" data-id="element-5828">6.8% of running time</div>
          </div>
        </Card>
        {/* Quality Losses */}
        <Card className="p-6" data-id="element-5829">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5830">
            Quality Losses Breakdown
          </h3>
          <div className="space-y-3" data-id="element-5831">
            {lossBreakdownData.quality.map((item, index) => <div key={index} data-id="element-5832">
                <div className="flex items-center justify-between mb-1" data-id="element-5833">
                  <span className="text-sm font-medium text-gray-700" data-id="element-5834">
                    {item.type}
                  </span>
                  <div className="text-right" data-id="element-5835">
                    <span className="text-sm font-semibold text-gray-800" data-id="element-5836">
                      {item.units}
                    </span>
                    <span className="text-xs text-gray-600 ml-2" data-id="element-5837">
                      ({item.percentage}%)
                    </span>
                  </div>
                </div>
                <div className="relative h-4 bg-gray-100 rounded overflow-hidden" data-id="element-5838">
                  <div className="h-full bg-blue-500" style={{
                width: `${item.percentage / 4.2 * 100}%`
              }} data-id="element-5839" />
                </div>
              </div>)}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200 text-center" data-id="element-5840">
            <div className="text-sm text-gray-600" data-id="element-5841">Total Quality Loss</div>
            <div className="text-2xl font-bold text-blue-600" data-id="element-5842">2,920 units</div>
            <div className="text-xs text-gray-500" data-id="element-5843">4.2% of production</div>
          </div>
        </Card>
      </div>
      {/* OEE Trend Chart */}
      <Card className="p-6 mb-8" data-id="element-5844">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-5845">
          OEE Trend Analysis (Last 12 Months)
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-5846">
          Historical OEE performance and component breakdown
        </p>
        <ResponsiveContainer width="100%" height={400} data-id="element-5847">
          <ComposedChart data={oeeTrendData} data-id="element-5848">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5849" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-5850" />
            <YAxis domain={[70, 100]} tick={{
            fontSize: 12
          }} label={{
            value: 'Percentage (%)',
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 12
            }
          }} data-id="element-5851" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-5852" />
            <Legend data-id="element-5853" />
            <Line type="monotone" dataKey="availability" stroke="#ef4444" strokeWidth={2} name="Availability %" dot={{
            r: 3
          }} data-id="element-5854" />
            <Line type="monotone" dataKey="performance" stroke="#f59e0b" strokeWidth={2} name="Performance %" dot={{
            r: 3
          }} data-id="element-5855" />
            <Line type="monotone" dataKey="quality" stroke="#3b82f6" strokeWidth={2} name="Quality %" dot={{
            r: 3
          }} data-id="element-5856" />
            <Line type="monotone" dataKey="oee" stroke="#8b5cf6" strokeWidth={3} name="OEE %" dot={{
            r: 5
          }} data-id="element-5857" />
            <Line type="monotone" dataKey="target" stroke="#10b981" strokeWidth={2} strokeDasharray="5 5" name="Target %" data-id="element-5858" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5859">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5860">
            <div className="text-center" data-id="element-5861">
              <div className="text-gray-600" data-id="element-5862">Current OEE</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-5863">80.8%</div>
              <div className="text-xs text-gray-500" data-id="element-5864">Dec 2024</div>
            </div>
            <div className="text-center" data-id="element-5865">
              <div className="text-gray-600" data-id="element-5866">YoY Growth</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5867">+7.4%</div>
              <div className="text-xs text-gray-500" data-id="element-5868">vs last year</div>
            </div>
            <div className="text-center" data-id="element-5869">
              <div className="text-gray-600" data-id="element-5870">Target</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5871">80%</div>
              <div className="text-xs text-gray-500" data-id="element-5872">achieved</div>
            </div>
            <div className="text-center" data-id="element-5873">
              <div className="text-gray-600" data-id="element-5874">Best Month</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-5875">Dec</div>
              <div className="text-xs text-gray-500" data-id="element-5876">80.8% OEE</div>
            </div>
            <div className="text-center" data-id="element-5877">
              <div className="text-gray-600" data-id="element-5878">World Class</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-5879">85%</div>
              <div className="text-xs text-gray-500" data-id="element-5880">gap: 4.2%</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Line-wise and Shift-wise OEE Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-5881">
        {/* Line-wise OEE Comparison */}
        <Card className="p-6" data-id="element-5882">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5883">
            Line-wise OEE Comparison
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-5884">
            <BarChart data={lineOEEData} data-id="element-5885">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5886" />
              <XAxis dataKey="line" tick={{
              fontSize: 11
            }} data-id="element-5887" />
              <YAxis domain={[0, 100]} tick={{
              fontSize: 11
            }} data-id="element-5888" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-5889" />
              <Legend data-id="element-5890" />
              <Bar dataKey="availability" fill="#ef4444" name="Availability %" data-id="element-5891" />
              <Bar dataKey="performance" fill="#f59e0b" name="Performance %" data-id="element-5892" />
              <Bar dataKey="quality" fill="#3b82f6" name="Quality %" data-id="element-5893" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5894">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5895">
              <div className="text-center" data-id="element-5896">
                <div className="text-gray-600" data-id="element-5897">Best Line</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5898">Line 2</div>
                <div className="text-xs text-gray-500" data-id="element-5899">82.0% OEE</div>
              </div>
              <div className="text-center" data-id="element-5900">
                <div className="text-gray-600" data-id="element-5901">Avg OEE</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-5902">78.1%</div>
                <div className="text-xs text-gray-500" data-id="element-5903">across all lines</div>
              </div>
              <div className="text-center" data-id="element-5904">
                <div className="text-gray-600" data-id="element-5905">Improvement Needed</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-5906">Line 3</div>
                <div className="text-xs text-gray-500" data-id="element-5907">75.0% OEE</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Shift-wise OEE Comparison */}
        <Card className="p-6" data-id="element-5908">
          <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5909">
            Shift-wise OEE Performance
          </h3>
          <ResponsiveContainer width="100%" height={350} data-id="element-5910">
            <ComposedChart data={shiftOEEData} data-id="element-5911">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-5912" />
              <XAxis dataKey="shift" tick={{
              fontSize: 10
            }} data-id="element-5913" />
              <YAxis domain={[0, 100]} tick={{
              fontSize: 11
            }} data-id="element-5914" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)}%`} data-id="element-5915" />
              <Legend data-id="element-5916" />
              <Bar dataKey="availability" fill="#ef4444" name="Availability %" data-id="element-5917" />
              <Bar dataKey="performance" fill="#f59e0b" name="Performance %" data-id="element-5918" />
              <Bar dataKey="quality" fill="#3b82f6" name="Quality %" data-id="element-5919" />
              <Line type="monotone" dataKey="oee" stroke="#8b5cf6" strokeWidth={3} name="OEE %" dot={{
              r: 5
            }} data-id="element-5920" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-5921">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-5922">
              <div className="text-center" data-id="element-5923">
                <div className="text-gray-600" data-id="element-5924">Best Shift</div>
                <div className="text-lg font-bold text-green-600" data-id="element-5925">Shift B</div>
                <div className="text-xs text-gray-500" data-id="element-5926">82.5% OEE</div>
              </div>
              <div className="text-center" data-id="element-5927">
                <div className="text-gray-600" data-id="element-5928">Avg OEE</div>
                <div className="text-lg font-bold text-gray-900" data-id="element-5929">78.5%</div>
                <div className="text-xs text-gray-500" data-id="element-5930">across all shifts</div>
              </div>
              <div className="text-center" data-id="element-5931">
                <div className="text-gray-600" data-id="element-5932">Focus Area</div>
                <div className="text-lg font-bold text-red-600" data-id="element-5933">Shift C</div>
                <div className="text-xs text-gray-500" data-id="element-5934">72.8% OEE</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* OEE by Plant Table */}
      <Card className="p-6" data-id="element-5935">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-5936">
          OEE Analysis by Plant
        </h3>
        <div className="overflow-x-auto" data-id="element-5937">
          <table className="w-full" data-id="element-5938">
            <thead data-id="element-5939">
              <tr className="border-b-2 border-gray-300" data-id="element-5940">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5941">
                  Plant
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5942">
                  OEE %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5943">
                  Availability %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5944">
                  Performance %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5945">
                  Quality %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5946">
                  Planned Time (hrs)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5947">
                  Actual Time (hrs)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5948">
                  Downtime (hrs)
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5949">
                  Ideal Cycles
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5950">
                  Actual Cycles
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5951">
                  Good Parts
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5952">
                  Defects
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5953">
                  Target %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-5954">
                  Gap %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-5955">
              {plantOEEData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-5956">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-5957">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5958">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getOEEColor(row.oee)}`} data-id="element-5959">
                      {row.oee.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5960">
                    {row.availability.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5961">
                    {row.performance.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5962">
                    {row.quality.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5963">
                    {row.plannedTime}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5964">
                    {row.actualTime}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-red-600 font-semibold" data-id="element-5965">
                    {row.downtime}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-5966">
                    {row.idealCycles.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-5967">
                    {row.actualCycles.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-700" data-id="element-5968">
                    {row.goodParts.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-red-600 font-semibold" data-id="element-5969">
                    {row.defects.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-5970">
                    {row.target}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-5971">
                    <span className={`text-sm font-bold ${getGapColor(row.gap)}`} data-id="element-5972">
                      {row.gap > 0 ? '+' : ''}
                      {row.gap.toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-5973">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-5974">AVERAGE</td>
                <td className="py-3 px-4 text-center" data-id="element-5975">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-5976">
                    78.1%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5977">
                  88.1%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5978">
                  91.9%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5979">
                  96.6%
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5980">
                  720
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5981">
                  634
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-5982">
                  86
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5983">
                  69,025
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-5984">
                  63,443
                </td>
                <td className="py-3 px-4 text-sm text-right text-green-700" data-id="element-5985">
                  61,308
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-5986">
                  2,135
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-5987">
                  80%
                </td>
                <td className="py-3 px-4 text-center text-sm text-red-600" data-id="element-5988">
                  -1.9%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-5989">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-5990">
            <div className="text-center" data-id="element-5991">
              <div className="text-gray-600" data-id="element-5992">Best Plant</div>
              <div className="text-xl font-bold text-green-600" data-id="element-5993">Chennai</div>
              <div className="text-xs text-gray-500" data-id="element-5994">82.0% OEE</div>
            </div>
            <div className="text-center" data-id="element-5995">
              <div className="text-gray-600" data-id="element-5996">Total Good Parts</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-5997">245,231</div>
              <div className="text-xs text-gray-500" data-id="element-5998">across all plants</div>
            </div>
            <div className="text-center" data-id="element-5999">
              <div className="text-gray-600" data-id="element-6000">Total Downtime</div>
              <div className="text-xl font-bold text-red-600" data-id="element-6001">344 hrs</div>
              <div className="text-xs text-gray-500" data-id="element-6002">this month</div>
            </div>
            <div className="text-center" data-id="element-6003">
              <div className="text-gray-600" data-id="element-6004">Total Defects</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-6005">8,539</div>
              <div className="text-xs text-gray-500" data-id="element-6006">3.4% defect rate</div>
            </div>
            <div className="text-center" data-id="element-6007">
              <div className="text-gray-600" data-id="element-6008">Improvement Needed</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-6009">Pune</div>
              <div className="text-xs text-gray-500" data-id="element-6010">-5.0% below target</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default OEEDashboard;